// Companion/chatSourceLabels.ts
//
// What the answer footer's source list (ORA-514, ORA-601) needs to know about
// a citation that the wire does not say outright: the label to fall back on
// when the server sent none, and whether the cited item is already a block in
// the conversation — in which case its chip reads "Shown above" and offers no
// plus.

import type { ChatBlock } from '@/slices/chat/chatSlice';
import type { ChatCitation } from '@/types/chatStream';
import { isPositiveSafeInteger } from './blocks/chatBlockTargets';

/**
 * Human label per citation kind, for a citation with no server-built `label`.
 */
export const KIND_LABELS: Record<string, string> = {
  article: 'Article',
  question: 'Question',
  flashcard: 'Flashcard',
  video: 'Video',
  document_page: 'Document',
  chat_document_page: 'Document',
  help_doc: 'Help doc',
  performance: 'Performance data',
  study_plan: 'Study plan',
  memory: 'AI Memory',
};

const OWN_DATA_KINDS = new Set(['performance', 'study_plan', 'memory']);

/**
 * The pill's text: the server's "Kind · short name" when it sent one, else the
 * kind label plus the page for a document page (the wire contract's fallback).
 */
export const citationLabel = (citation: ChatCitation): string => {
  // These labels have changed over time. Own-data citations have no content
  // title to preserve, so render their current name even in saved history.
  if (OWN_DATA_KINDS.has(citation.kind)) return KIND_LABELS[citation.kind];

  if (citation.label) return citation.label;

  const kind = KIND_LABELS[citation.kind] ?? citation.kind;

  return isPositiveSafeInteger(citation.page)
    ? `${kind} · p.${citation.page}`
    : kind;
};

/**
 * The key a citation and a block share when they are the same item. Null for
 * a kind that is never a block (own data) or a citation missing its id.
 */
export const citationContentKey = (citation: ChatCitation): string | null => {
  if (!citation.contentId) return null;

  if (
    citation.kind === 'document_page' ||
    citation.kind === 'chat_document_page'
  ) {
    return isPositiveSafeInteger(citation.page)
      ? `${citation.kind}:${citation.contentId}#${citation.page}`
      : null;
  }

  return `${citation.kind}:${citation.contentId}`;
};

const asString = (value: unknown): string =>
  typeof value === 'string' ? value : '';

/**
 * Every citation key one block can stand in for. A flashcard deck block shows
 * several cards, so it answers for each of them; the rest carry one id.
 */
export const blockContentKeys = (block: ChatBlock): string[] => {
  const { kind, payload } = block;

  switch (kind) {
    case 'question': {
      const id = asString(payload.vignetteId);
      return id ? [`question:${id}`] : [];
    }
    case 'flashcards':
      return Array.isArray(payload.cards)
        ? payload.cards.flatMap((card) => {
            const id =
              typeof card === 'object' && card !== null
                ? asString((card as Record<string, unknown>).id)
                : '';

            return id ? [`flashcard:${id}`] : [];
          })
        : [];
    case 'article': {
      const id = asString(payload.articleId);
      return id ? [`article:${id}`] : [];
    }
    case 'video': {
      const id = asString(payload.videoId);
      return id ? [`video:${id}`] : [];
    }
    case 'help_doc': {
      const slug = asString(payload.slug);
      return slug ? [`help_doc:${slug}`] : [];
    }
    case 'document_excerpt': {
      // mcgraw-hill: a quoted chat-document page stands in for its citation.
      const documentId =
        payload.source === 'chat_document' ? asString(payload.documentId) : '';

      if (documentId) {
        return isPositiveSafeInteger(payload.page)
          ? [`chat_document_page:${documentId}#${payload.page}`]
          : [];
      }

      const lectureId = asString(payload.lectureId);
      return lectureId && isPositiveSafeInteger(payload.page)
        ? [`document_page:${lectureId}#${payload.page}`]
        : [];
    }
    default:
      return [];
  }
};

/**
 * For each citation whose item is already a block somewhere in the
 * conversation, the id of that block — a block the model served in a later
 * turn counts as much as one the student added, and both survive a reload.
 *
 * The block id is what lets the footer's "Shown above" chip take the student
 * back to the block instead of offering to add a second copy of it (ORA-601).
 * A citation with no block is simply absent from the map.
 */
export const citationBlockIds = (
  citations: ChatCitation[],
  conversationBlocks: ChatBlock[]
): Map<string, string> => {
  const blockIdByKey = new Map<string, string>();

  conversationBlocks.forEach((block) => {
    blockContentKeys(block).forEach((key) => {
      // The first block to show the item is the one to go back to.
      if (!blockIdByKey.has(key)) blockIdByKey.set(key, block.id);
    });
  });

  return new Map(
    citations.flatMap((citation): Array<[string, string]> => {
      const key = citationContentKey(citation);
      const blockId = key === null ? undefined : blockIdByKey.get(key);
      return blockId ? [[citation.id, blockId]] : [];
    })
  );
};
